/**
 * The rooms, drawn with the renderer that draws everything else.
 *
 * These were CSS gradients first and looked like CSS gradients: smooth ramps
 * and soft ellipses behind sprites made of hard pixels. A room is now a spec,
 * so it goes through the same shapes, the same kit and the same renderer as the
 * things standing in it, and comes out on the same grid.
 *
 * They are built at the size the screen needs rather than at a fixed size,
 * which a format made of insets from edges gets for free: a floor is "the
 * bottom four tile rows", not "rows 130 to 160".
 *
 * The projection is the one the colony game uses: a floor of 32px tiles seen
 * from above, a wall behind it, and things standing on tiles that extend up
 * above them. That is what makes a room read as a room. It also decides which
 * sprites can go in one, because a top-down rug and a head-on lantern cannot
 * share a floor.
 */

import type { SpriteSpec } from "../src/spec.js";

type Rgba = [number, number, number, number];
type Roles = Record<string, Rgba>;
type Part = { shape: string; [field: string]: unknown };

const c = (hex: string): Rgba => [
	parseInt(hex.slice(1, 3), 16),
	parseInt(hex.slice(3, 5), 16),
	parseInt(hex.slice(5, 7), 16),
	255,
];

/** Every kit needs these two, and no scene is outlined as a whole. */
const base: Roles = { none: [0, 0, 0, 0], outline: c("#14101c") };

/** One floor tile, in room pixels. The size the game's own art is drawn to. */
export const TILE = 32;

/** The largest a spec may be on either side. Comes from the validator. */
const MAX_SIDE = 512;

/** The widest a room can be, in tiles, and therefore the most it can hold. */
const MAX_COLS = Math.floor(MAX_SIDE / TILE);

/**
 * How wide a room is, in tiles, however small the screen is.
 *
 * The rooms are composed for this many columns: the placements in welcome.ts
 * are fractions along it, and a dozen things spread over fifteen tiles is a
 * room. Tie the width to the viewport instead and a phone gets three columns
 * for the same dozen things, which is what it did, and they land in a heap.
 */
const COLS = 15;

/** Tiles that have to be on screen at once, or a room reads as a close-up. */
const VISIBLE = 6;

/**
 * How big one room pixel is on screen, and how many of them the room is.
 *
 * Two separate questions, and treating them as one is what broke the phone. The
 * scale is how big things look; the width is how much room there is. A room is
 * always at least COLS tiles wide and is allowed to be wider than the window,
 * so on a narrow screen you walk through a room rather than being shown a
 * squashed one. The composition is then the same everywhere.
 *
 * The scale is picked from the height so a room is a comfortable number of rows
 * tall, then stepped down until enough of the floor is on screen at once. It
 * has to be a whole number or the scene stops being made of pixels.
 *
 * The floor of `fits` is the part that is easy to miss: a spec is capped at 512
 * pixels a side, and choosing the scale from the height alone quietly produced
 * a 640-wide room on a wide monitor. The validator refused it and took the
 * whole page down with it. It is also what keeps the room covering the window,
 * since a scale of at least ceil(vw / 512) makes MAX_COLS tiles reach across.
 * Pinned by src/rooms.test.ts.
 */
export function roomSize(vw: number, vh: number) {
	const fits = Math.max(Math.ceil(vw / MAX_SIDE), Math.ceil(vh / MAX_SIDE));
	let scale = Math.max(3, fits, Math.min(6, Math.round(vh / 210)));
	// A phone at scale 4 sees three tiles, which is a corner of a room rather
	// than a room. Down to 2 if that is what it takes, but never below what the
	// 512 cap needs.
	while (scale > Math.max(2, fits) && vw / (TILE * scale) < VISIBLE) scale -= 1;
	const cols = Math.min(MAX_COLS, Math.max(COLS, Math.ceil(vw / scale / TILE)));
	return { scale, cols, w: cols * TILE, h: Math.ceil(vh / scale) };
}

export interface Scene {
	/** Rows of floor tiles. Things stand on one of them. */
	rows: number;
	/** Where the wall stops and the floor starts, in rows from the top. */
	line: number;
	spec: SpriteSpec;
}

interface Look {
	name: string;
	roles: Roles;
	/** Tile rows of floor. Fewer rows means a shallower room. */
	rows?: number;
	/** Behind the floor: a sky, a wall, whatever this place has. */
	back: (ctx: Ctx) => Part[];
	/** The floor itself, under the tile grid. */
	floor: (ctx: Ctx) => Part[];
	/** The colour the tile grid is drawn in. */
	grid: string;
	/** Off for a room whose floor is a tiled sprite, which draws its own edges. */
	gridded?: boolean;
}

interface Ctx {
	w: number;
	h: number;
	/** Where the wall stops and the floor starts. */
	line: number;
	rows: number;
	/** A part covering rows y0 to y1, full width. */
	band: (y0: number, y1: number, role: string) => Part;
	/** A part in a box given as left, top, width, height. */
	box: (x: number, y: number, bw: number, bh: number, part: Part) => Part;
}

type Build = (w: number, h: number) => Scene;

/**
 * Everything every room has: a back, a floor, and a grid of tiles over it.
 *
 * The grid is the load-bearing part. It is what tells you the floor recedes
 * rather than being a wall you are looking at, and it is what the placements in
 * alt.ts count along, so a thing put on tile 3 of row 1 lands on the tile you
 * can see.
 */
function roomScene(look: Look): Build {
	return (w, h) => {
		const rows = look.rows ?? Math.max(2, Math.min(5, Math.floor((h * 0.62) / TILE)));
		const line = h - rows * TILE;

		const band = (y0: number, y1: number, role: string): Part => ({
			shape: "panel",
			inset: [0, y0, 0, h - y1],
			role,
			borderRole: "none",
		});
		const box = (x: number, y: number, bw: number, bh: number, part: Part): Part => ({
			...part,
			inset: [x, y, Math.max(0, w - x - bw), Math.max(0, h - y - bh)],
		});
		const ctx: Ctx = { w, h, line, rows, band, box };

		return {
			rows,
			line,
			spec: {
				name: `room_${look.name}`,
				size: [w, h],
				kit: { name: `room_${look.name}`, roles: { ...base, ...look.roles } },
				outline: false,
				parts: [
					...look.back(ctx),
					...look.floor(ctx),
					// The tile grid, both ways. Only drawn when the room has no floor
					// sprite to tile: a real tile carries its own edges, and a grid
					// ruled over the top of one is the thing that made this look like a
					// spreadsheet rather than a floor.
					...(look.gridded === false
						? []
						: [
								{ shape: "grille", inset: [0, line, 0, 0], step: TILE, role: look.grid, gapRole: "none", direction: "rows" },
								{ shape: "grille", inset: [0, line, 0, 0], step: TILE, role: look.grid, gapRole: "none", direction: "columns" },
							]),
				],
			},
		};
	};
}

// ---- the village yard, at dusk ----------------------------------------------------------

const dusk = roomScene({
	name: "dusk",
	grid: "cobble_dark",
	gridded: false,
	roles: {
		cloud: c("#5a4a6e"),
		cloud_lit: c("#7a6489"),
		sky_high: c("#191340"),
		sky_low: c("#e8a06a"),
		moon: c("#ffefcf"),
		moon_lit: c("#fffaf0"),
		wall: c("#4a4152"),
		wall_lit: c("#635668"),
		wall_dark: c("#2e2838"),
		cobble: c("#5b5344"),
		cobble_lit: c("#6f6653"),
		cobble_dark: c("#3d382e"),
	},
	// Two bands of cloud crossing the dusk sky at different heights and rates.
	back: ({ w, h, line, band, box }) => [
		// A banded sky rather than a smooth one. Dithered bands are what a sky
		// looks like in this format, and a smooth ramp is what made the old
		// backgrounds read as a web page behind a sprite.
		{ shape: "gradient", inset: [0, 0, 0, h - line], from: "sky_high", to: "sky_low", bands: 9 },
		box(Math.round(w * 0.78), Math.round(line * 0.16), 18, 18, { shape: "disc", role: "moon", borderRole: "none", litRole: "moon_lit" }),
		// A low boundary wall, which is what turns a field into a yard.
		band(line - 26, line, "wall"),
		band(line - 26, line - 23, "wall_lit"),
		{ shape: "grille", inset: [0, line - 23, 0, h - line], step: 24, role: "wall_dark", gapRole: "none", direction: "columns" },
		band(line - 3, line, "wall_dark"),
	],
	floor: ({ h, line, band }) => [
		band(line, h, "cobble"),
		band(line, line + 2, "cobble_lit"),
	],
});

// ---- the garden, at noon -----------------------------------------------------------------

const garden = roomScene({
	name: "garden",
	gridded: false,
	grid: "grass_dark",
	roles: {
		cloud: c("#ffffff"),
		bird: c("#39323f"),
		sky_high: c("#3f95d2"),
		sky_low: c("#cfeaf7"),
		sun: c("#fff6d2"),
		sun_lit: c("#fffdf4"),
		hedge: c("#2f5a2c"),
		hedge_lit: c("#4c8340"),
		hedge_dark: c("#1e3c1d"),
		grass: c("#5c9a3c"),
		grass_lit: c("#7ab84e"),
		grass_dark: c("#3f7029"),
	},
	// Fair weather cloud, and birds crossing higher up.
	back: ({ w, h, line, band, box }) => [
		{ shape: "gradient", inset: [0, 0, 0, h - line], from: "sky_high", to: "sky_low", bands: 7 },
		box(Math.round(w * 0.14), Math.round(line * 0.14), 16, 16, { shape: "disc", role: "sun", borderRole: "none", litRole: "sun_lit" }),
		// A hedge along the back, with its top broken up so it is planting rather
		// than a green rectangle.
		band(line - 30, line, "hedge"),
		{ shape: "grille", inset: [0, line - 30, 0, h - line + 22], step: 5, role: "hedge_lit", gapRole: "none", direction: "columns" },
		{ shape: "grille", inset: [0, line - 22, 0, h - line], step: 7, role: "hedge_dark", gapRole: "none", direction: "columns" },
		band(line - 3, line, "hedge_dark"),
	],
	floor: ({ h, line, band }) => [
		band(line, h, "grass"),
		band(line, line + 2, "grass_lit"),
	],
});

// ---- inside, lamps lit ----------------------------------------------------------------------

const inside = roomScene({
	name: "inside",
	grid: "board_dark",
	gridded: false,
	roles: {
		wall: c("#6d5341"),
		wall_top: c("#5a4436"),
		batten: c("#4a382c"),
		rail: c("#8a6a4c"),
		skirt: c("#3d2d23"),
		board: c("#7a5433"),
		board_lit: c("#946742"),
		board_dark: c("#57391f"),
	},
	// Both of these are painted over by tiled sprites when the room has them.
	// What is left underneath is a plain wash, so a missing tile shows as a dull
	// room rather than as a hole.
	back: ({ line, band }) => [band(0, line, "wall")],
	floor: ({ h, line, band }) => [band(line, h, "board")],
});

// ---- the sea floor ---------------------------------------------------------------------------

const water = roomScene({
	name: "water",
	gridded: false,
	grid: "sand_dark",
	roles: {
		fish: c("#e8a34a"),
		fish_pale: c("#8fd4d8"),
		sea_high: c("#1a7f92"),
		sea_low: c("#063348"),
		rock: c("#0d3c4f"),
		rock_lit: c("#18596c"),
		rock_dark: c("#082735"),
		sand: c("#c2a874"),
		sand_lit: c("#dcc38d"),
		sand_dark: c("#937c4d"),
	},
	// Fish crossing the open water, and a shoal further back.
	back: ({ h, line, band }) => [
		// Light comes from the surface, so this one is bright at the top.
		{ shape: "gradient", inset: [0, 0, 0, h - line], from: "sea_high", to: "sea_low", bands: 8 },
		band(line - 34, line, "rock"),
		{ shape: "grille", inset: [0, line - 34, 0, h - line + 20], step: 11, role: "rock_lit", gapRole: "none", direction: "columns" },
		{ shape: "grille", inset: [0, line - 20, 0, h - line], step: 9, role: "rock_dark", gapRole: "none", direction: "columns" },
		band(line - 3, line, "rock_dark"),
	],
	floor: ({ h, line, band }) => [
		band(line, h, "sand"),
		band(line, line + 2, "sand_lit"),
	],
});

// ---- the ship, in orbit -------------------------------------------------------------------------

const orbit = roomScene({
	name: "orbit",
	gridded: false,
	grid: "deck_dark",
	roles: {
		far_ship: c("#9fb4d8"),
		hull: c("#2c3040"),
		hull_lit: c("#3d4457"),
		hull_dark: c("#1b1e29"),
		frame: c("#454c62"),
		space: c("#05040d"),
		planet: c("#2c5183"),
		planet_lit: c("#4a7cb4"),
		deck: c("#3a3f52"),
		deck_lit: c("#4b5168"),
		deck_dark: c("#262a38"),
		strip: c("#3fc2d8"),
	},
	// Something passing the viewport, a long way off.
	back: ({ w, h, line, band, box }) => [
		band(0, line, "hull"),
		{ shape: "grille", inset: [0, 0, 0, h - line], step: 26, role: "hull_dark", gapRole: "none", direction: "columns" },
		// A lit strip along the top of the wall, which is the signature the game's
		// own art puts on everything fabricated.
		band(line - 22, line - 20, "strip"),
		band(line - 18, line, "hull_lit"),
		band(line - 4, line, "hull_dark"),
	],
	floor: ({ h, line, band }) => [
		band(line, h, "deck"),
		band(line, line + 2, "deck_lit"),
	],
});

export const SCENES: Record<string, Build> = { dusk, garden, inside, water, orbit };

/**
 * The blob under a thing, so it sits on the floor rather than in front of it.
 *
 * Built as a spec like everything else, because a CSS ellipse would be the one
 * soft-edged thing in a picture made of pixels, which is exactly the fault the
 * scenery had.
 */
export function shadowSpec(width: number): SpriteSpec {
	const wide = Math.max(6, Math.round(width * 0.8));
	const tall = Math.max(4, Math.round(wide * 0.34));
	return {
		name: "shadow",
		size: [wide, tall],
		kit: { name: "shadow", roles: { none: [0, 0, 0, 0], shade: [8, 6, 14, 90] } },
		outline: false,
		parts: [{ shape: "disc", inset: [0, 0, 0, 0], role: "shade", borderRole: "none", litRole: "none" }],
	};
}
